import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Table, Button, Space, Select, Modal, Form, DatePicker, InputNumber, Input,
  AutoComplete, Tag, message, Popconfirm, Descriptions, Divider, Radio, Steps, Tooltip,
} from 'antd';
import {
  ReloadOutlined, PlusOutlined, CheckOutlined, CloseOutlined, EyeOutlined, DeleteOutlined,
  PrinterOutlined, SettingOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useFinanceApi, entityOptions } from '../api/financeApi';
import { fmtAmount, fmtDate } from '../shared/format';
import { formatNumberTR, inputNumberMoneyProps } from '../../../utils/currency';
import { enhanceColumns, toplamGenislik } from '../../ortak/tableHelpers';

// maliyetler.masraf_tipi kodları — sql_queries.MASRAF_TIPLERI ile birebir (0 Kabin Maliyeti ve
// 10 Proje/İhale hakediş kaleminde seçilmez). Önceki etiketler (Enerji/Bakım, Montaj, Tadilat,
// Kira, Diğer) backend kodlarıyla uyuşmuyordu: onayda maliyetler'e ör. 'Montaj' 4 = Reklam,
// 'Tadilat' 5 = Kazı olarak yazılıyor, raporlarda yanlış tipte görünüyordu.
const MASRAF_TIPI = {
  1: 'Altyapı', 2: 'Enerji/Data', 3: 'Nakliye', 4: 'Reklam', 5: 'Kazı', 6: 'Çevre', 7: 'İlave Maliyet',
  8: 'Tahliye', 9: 'Nakliye (Tahliye)', 11: 'Bakım Onarım', 12: 'Temizlik', 13: 'Keşif Maliyeti',
  14: 'Temsil Ağırlama Maliyeti', 15: 'Lokasyon Personel Maliyeti', 16: 'Diğer Hizmetler',
};
const DURUM = {
  onay_bekliyor: { label: 'Onay Bekliyor', color: 'gold' },
  onaylandi: { label: 'Onaylandı', color: 'green' },
  reddedildi: { label: 'Reddedildi', color: 'red' },
  odendi: { label: 'Ödendi', color: 'blue' },
};
const ROL_LABEL = { operasyon: 'Operasyon', satinalma: 'Satın Alma', finans: 'Finans', yonetim: 'Yönetim' };

// Backend _hakedis_hesapla ile birebir aynı (canlı önizleme için)
function hesaplaTutar(ara, kdvOrani, kdvDahil, tevOran) {
  const a = Number(ara || 0);
  const ko = Number(kdvOrani || 0) / 100;
  const tev = Number(tevOran || 0);
  let matrah;
  let kdv;
  if (kdvDahil) { matrah = ko ? a / (1 + ko) : a; kdv = a - matrah; }
  else { matrah = a; kdv = a * ko; }
  const tevkifat = kdv * tev;
  return { matrah, kdv, tevkifat, odenecek: matrah + kdv - tevkifat };
}

// "HAKEDİŞ BELGESİ" yazdırılabilir çıktı — ayrı pencerede HTML + window.print()
function printHakedisBelgesi(d) {
  if (!d) return;
  const kdvOran = Number(d.kdv_orani || 20);
  const kalemler = d.kalemler || [];
  const fmt = (n) => formatNumberTR(n || 0);
  const dd = (s) => (s ? dayjs(s).format('DD.MM.YYYY') : '');
  const esc = (v) => (v == null ? '' : String(v).replace(/[&<>]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c])));
  const ara = kalemler.reduce((s, k) => s + Number(k.tutar || 0), 0);
  const kdv = ara * kdvOran / 100;
  const genel = ara + kdv;
  const belgeNo = `HKD-${String(d.id ?? '').padStart(5, '0')}`;
  const rows = kalemler.map((k, i) => `
    <tr>
      <td class="c">${i + 1}</td><td>${esc(k.maptriksID)}</td><td>${esc(k.lokasyon_ad)}</td>
      <td>${esc(k.il)}</td><td>${esc(k.ilce)}</td>
      <td class="c">${k.kabin ? '#' + esc(k.kabin) : 'Tümü'}</td>
      <td class="c">${esc(k.platform) || '-'}</td>
      <td class="c">${esc(k.qr) || '-'}</td><td>${esc(MASRAF_TIPI[k.masraf_tipi] || '')}</td>
      <td>${esc(k.islem_adi) || '-'}</td><td>${esc(k.aciklama) || '-'}</td><td class="c">${dd(k.masraf_tarihi)}</td>
      <td class="num">${fmt(k.tutar)}</td><td class="num">${fmt(Number(k.tutar || 0) * kdvOran / 100)}</td>
    </tr>`).join('');
  const box = (title, rowsHtml) => `<div class="box"><h3>${title}</h3>${rowsHtml}</div>`;
  const r = (lbl, val) => `<div class="row"><div class="lbl">${lbl}</div><div class="val">${esc(val)}</div></div>`;
  const html = `<!doctype html><html lang="tr"><head><meta charset="utf-8"><title>${belgeNo}</title>
  <style>
    *{box-sizing:border-box}
    body{font-family:Arial,"Segoe UI",sans-serif;color:#1f2d3d;margin:22px;font-size:12px}
    .head{display:flex;justify-content:space-between;align-items:flex-start}
    .head h1{margin:0;font-size:22px;letter-spacing:.5px}
    .head .belge{color:#6b7785;margin-top:4px}
    .head .cari{text-align:right;font-weight:bold;font-size:16px;max-width:280px}
    .rule{height:3px;background:#2c3e50;margin:10px 0 18px}
    .boxes{display:flex;gap:12px;margin-bottom:16px}
    .box{flex:1;border:1px solid #e3e7eb;border-radius:8px;padding:12px 14px}
    .box h3{margin:0 0 8px;font-size:11px;color:#8a96a3;letter-spacing:.5px;text-transform:uppercase}
    .box .row{display:flex;margin-bottom:3px}
    .box .row .lbl{width:96px;color:#6b7785;flex-shrink:0}
    .box .row .val{flex:1;font-weight:600;word-break:break-word}
    table{width:100%;border-collapse:collapse;margin-top:4px}
    thead th{background:#2c3e50;color:#fff;padding:7px 6px;text-align:left;font-size:11px}
    thead th.num{text-align:right}
    tbody td{border:1px solid #e3e7eb;padding:6px}
    tbody td.num{text-align:right;white-space:nowrap}
    tbody td.c{text-align:center}
    tfoot td{padding:7px 8px;font-weight:bold;text-align:right;border:1px solid #e3e7eb}
    .t-ara td{background:#eef1f4}
    .t-kdv td{background:#fdf7e3}
    .t-genel td{background:#e3f0e1;font-size:13px}
    .aciklama{margin-top:14px;background:#f6f8fa;border:1px solid #e3e7eb;border-radius:6px;padding:10px 12px}
    .signs{display:flex;justify-content:space-between;margin-top:64px}
    .signs .s{width:30%;text-align:center}
    .signs .s .line{border-top:1px solid #2c3e50;padding-top:6px;color:#6b7785}
    @page{size:A4 landscape;margin:12mm}
    @media print{body{margin:0}}
  </style></head><body>
    <div class="head">
      <div><h1>HAKEDİŞ BELGESİ</h1><div class="belge">Belge No: ${belgeNo}</div></div>
      <div class="cari">${esc((d.cari_ad || '').toLocaleUpperCase('tr-TR'))}</div>
    </div>
    <div class="rule"></div>
    <div class="boxes">
      ${box('Firma Bilgileri', r('Firma:', d.firma_adi) + r('Adres:', d.firma_adres) + r('Telefon:', d.firma_tel) + r('E-posta:', d.firma_mail) + r('Vergi No:', d.firma_vergi_no))}
      ${box('Cari Hesap / Taşeron', r('Cari:', d.cari_ad) + r('Vergi No:', d.cari_vergi_no) + r('Telefon:', d.cari_telefon) + r('IBAN:', d.cari_iban))}
      ${box('Belge Bilgileri', r('Hakediş Tarihi:', dd(d.tarih)) + r('Düzenleme:', dd(d.kayit_tarihi || d.tarih)) + r('SAT No:', d.sat_no) + r('Fatura Kodu:', d.fatura_kodu) + r('Oluşturan:', d.olusturan))}
    </div>
    <table>
      <thead><tr>
        <th>#</th><th>MaptriksID</th><th>Lokasyon</th><th>İl</th><th>İlçe</th><th>Kabin</th><th>Platform</th><th>QR</th>
        <th>Yapılacak İş</th><th>İşlem Açıklaması</th><th>Açıklama</th><th>İşlem Tarihi</th><th class="num">Tutar (₺)</th><th class="num">KDV (₺)</th>
      </tr></thead>
      <tbody>${rows}</tbody>
      <tfoot>
        <tr class="t-ara"><td colspan="12">ARA TOPLAM</td><td class="num">${fmt(ara)}</td><td class="num">${fmt(kdv)}</td></tr>
        <tr class="t-kdv"><td colspan="13">KDV (%${kdvOran})</td><td class="num">${fmt(kdv)} ₺</td></tr>
        <tr class="t-genel"><td colspan="13">GENEL TOPLAM</td><td class="num">${fmt(genel)} ₺</td></tr>
      </tfoot>
    </table>
    ${d.aciklama ? `<div class="aciklama"><b>AÇIKLAMA:</b> ${esc(d.aciklama)}</div>` : ''}
    <div class="signs">
      <div class="s"><div class="line">Hazırlayan</div></div>
      <div class="s"><div class="line">Kontrol Eden</div></div>
      <div class="s"><div class="line">Onaylayan</div></div>
    </div>
    <script>window.onload=function(){setTimeout(function(){window.print();},250);};</script>
  </body></html>`;
  const w = window.open('', '_blank', 'width=1150,height=820');
  if (!w) { message.error('Yazdırma penceresi açılamadı (popup engellenmiş olabilir)'); return; }
  w.document.write(html);
  w.document.close();
}

export default function HakedisTab({ lookups, perms }) {
  const api = useFinanceApi();
  const p = perms.hakedis || {};
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [durum, setDurum] = useState(null);

  const [createOpen, setCreateOpen] = useState(false);
  const [configOpen, setConfigOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [rejectRow, setRejectRow] = useState(null);
  const [tevOptions, setTevOptions] = useState([]);

  useEffect(() => {
    if (p.view === false) return;
    api.get('/tevkifat-kodlari').then((r) => setTevOptions(r || [])).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    if (p.view === false) return;
    setLoading(true);
    try {
      setList(await api.get('/accruals', durum ? { durum } : {}));
    } catch (e) {
      message.error(e?.response?.data?.detail || 'Hakedişler yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, [durum]);

  useEffect(() => { load(); }, [load]);

  if (p.view === false) return <div style={{ padding: 24 }}>Görüntüleme yetkiniz yok.</div>;

  const openDetail = async (row) => {
    try {
      const [d, onay] = await Promise.all([
        api.get(`/accruals/${row.id}`),
        api.get(`/accruals/${row.id}/onay-durumu`).catch(() => null),
      ]);
      setDetail({ ...d, _onay: onay });
    } catch (e) {
      message.error('Detay alınamadı');
    }
  };

  const printRow = async (row) => {
    try {
      printHakedisBelgesi(await api.get(`/accruals/${row.id}`));
    } catch (e) {
      message.error('Belge alınamadı');
    }
  };

  const approve = async (row) => {
    try {
      await api.post(`/accruals/${row.id}/approve`);
      message.success('Onaylandı');
      load();
    } catch (e) {
      message.error(e?.response?.data?.detail || 'Onay başarısız');
    }
  };

  const doReject = async (sebep) => {
    try {
      await api.post(`/accruals/${rejectRow.id}/reject`, { red_sebebi: sebep });
      message.success('Reddedildi');
      setRejectRow(null);
      load();
    } catch (e) {
      message.error(e?.response?.data?.detail || 'Red başarısız');
    }
  };

  const columns = [
    { title: '#', dataIndex: 'id', width: 60 },
    { title: 'Tarih', dataIndex: 'tarih', render: fmtDate },
    { title: 'Cari', dataIndex: 'cari_ad', ellipsis: true },
    { title: 'Firma', dataIndex: 'firma_adi', ellipsis: true },
    { title: 'Matrah', dataIndex: 'toplam_tutar', render: (v) => fmtAmount(v, 'TRY') },
    { title: 'Ödenecek', dataIndex: 'odenecek_tutar', render: (v) => fmtAmount(v, 'TRY') },
    { title: 'Fatura Kodu', dataIndex: 'fatura_kodu', width: 110, render: (v) => v || '-' },
    { title: 'KDV %', dataIndex: 'kdv_orani', width: 70 },
    { title: 'Tevkifat', dataIndex: 'tevkifat_kodu', width: 90, render: (v) => v || '-' },
    {
      title: 'Durum', dataIndex: 'durum', width: 150,
      render: (v, r) => (
        <Tag color={(DURUM[v] || {}).color}>
          {(DURUM[v] || {}).label || v}
          {v === 'onay_bekliyor' && r.onay_adim ? ` · ${ROL_LABEL[r.onay_adim] || r.onay_adim}` : ''}
        </Tag>
      ),
    },
    {
      title: 'İşlem', key: 'a', fixed: 'right', width: 260,
      render: (_, r) => (
        <Space size={4}>
          <Button size="small" icon={<EyeOutlined />} onClick={() => openDetail(r)} title="Detay" />
          <Button size="small" icon={<PrinterOutlined />} onClick={() => printRow(r)} title="Yazdır" />
          {p.edit !== false && r.durum === 'onay_bekliyor' && (
            <>
              <Popconfirm title="Bu adımı onaylıyor musunuz? (Son adımda maliyet + ödeme talebi oluşur)" okText="Onayla" cancelText="Vazgeç" onConfirm={() => approve(r)}>
                <Button size="small" type="primary" icon={<CheckOutlined />}>Onayla</Button>
              </Popconfirm>
              <Button size="small" danger icon={<CloseOutlined />} onClick={() => setRejectRow(r)}>Reddet</Button>
            </>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Space wrap style={{ marginBottom: 12 }}>
        <Select allowClear placeholder="Durum" style={{ width: 160 }} value={durum} onChange={setDurum}
          options={Object.entries(DURUM).map(([v, o]) => ({ value: v, label: o.label }))} />
        <Button icon={<ReloadOutlined />} onClick={load}>Yenile</Button>
        {p.create !== false && (
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateOpen(true)}>Yeni Hakediş</Button>
        )}
        {p.edit !== false && (
          <Tooltip title="Kademeli onay bantları ve rol→pozisyon ayarları">
            <Button icon={<SettingOutlined />} onClick={() => setConfigOpen(true)}>Onay Akışı</Button>
          </Tooltip>
        )}
        <span style={{ color: '#888' }}>{list.length} kayıt</span>
      </Space>

      <Table rowKey="id" size="small" loading={loading} dataSource={list} columns={enhanceColumns(columns)}
        scroll={{ x: toplamGenislik(enhanceColumns(columns)), y: 480 }} pagination={{ pageSize: 50 }} />

      {createOpen && (
        <HakedisCreateModal
          lookups={lookups}
          tevOptions={tevOptions}
          onClose={() => setCreateOpen(false)}
          onDone={() => { setCreateOpen(false); load(); }}
        />
      )}

      {configOpen && <HakedisOnayConfigModal onClose={() => setConfigOpen(false)} />}

      <Modal title={`Hakediş #${detail?.id || ''}`} open={!!detail}
        footer={[
          <Button key="print" type="primary" icon={<PrinterOutlined />} onClick={() => printHakedisBelgesi(detail)}>Yazdır</Button>,
          <Button key="close" onClick={() => setDetail(null)}>Kapat</Button>,
        ]}
        onCancel={() => setDetail(null)} width={820} destroyOnClose>
        {detail && (
          <>
            <Descriptions size="small" column={2} bordered>
              <Descriptions.Item label="Cari">{detail.cari_ad}</Descriptions.Item>
              <Descriptions.Item label="Firma">{detail.firma_adi}</Descriptions.Item>
              <Descriptions.Item label="Tarih">{fmtDate(detail.tarih)}</Descriptions.Item>
              <Descriptions.Item label="KDV">%{detail.kdv_orani} {detail.kdv_dahil ? '(Dahil)' : '(Hariç)'}</Descriptions.Item>
              <Descriptions.Item label="Matrah (KDV hariç)">{fmtAmount(detail.matrah ?? detail.toplam_tutar, 'TRY')}</Descriptions.Item>
              <Descriptions.Item label="KDV Tutarı">{fmtAmount(detail.kdv_tutar, 'TRY')}</Descriptions.Item>
              <Descriptions.Item label="Tevkifat">
                {detail.tevkifat_kodu ? `${detail.tevkifat_kodu} · ${fmtAmount(detail.tevkifat_tutar, 'TRY')}` : '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Ödenecek"><b>{fmtAmount(detail.odenecek_tutar, 'TRY')}</b></Descriptions.Item>
              <Descriptions.Item label="Durum">
                <Tag color={(DURUM[detail.durum] || {}).color}>{(DURUM[detail.durum] || {}).label || detail.durum}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="SAT / Fatura">
                {detail.sat_no || '-'}{detail.fatura_kodu ? ` / ${detail.fatura_kodu}` : ''}
              </Descriptions.Item>
              {detail.aciklama ? (
                <Descriptions.Item label="Açıklama" span={2}>{detail.aciklama}</Descriptions.Item>
              ) : null}
            </Descriptions>
            {detail._onay?.chain?.length > 0 && (
              <>
                <Divider>Onay Akışı</Divider>
                <OnayZinciriSteps onay={detail._onay} />
              </>
            )}
            <Divider>Kalemler</Divider>
            <Table rowKey="id" size="small" pagination={false} dataSource={detail.kalemler || []}
              columns={[
                { title: 'Lokasyon', dataIndex: 'lokasyon_ad', ellipsis: true },
                { title: 'Maptriks', dataIndex: 'maptriksID' },
                { title: 'Platform', dataIndex: 'kabin', width: 90, render: (v) => (v ? `#${v}` : 'Tümü') },
                { title: 'Masraf', dataIndex: 'masraf_tipi', render: (v) => MASRAF_TIPI[v] || v },
                { title: 'İşlem', dataIndex: 'islem_adi', ellipsis: true },
                { title: 'Açıklama', dataIndex: 'aciklama', ellipsis: true },
                { title: 'Tarih', dataIndex: 'masraf_tarihi', render: fmtDate },
                { title: 'Tutar', dataIndex: 'tutar', render: (v) => fmtAmount(v, 'TRY') },
              ]} />
          </>
        )}
      </Modal>

      <RejectModal row={rejectRow} onClose={() => setRejectRow(null)} onConfirm={doReject} />
    </div>
  );
}

function OnayZinciriSteps({ onay }) {
  const chain = onay?.chain || [];
  const zinciri = onay?.zinciri || [];
  const onaylanan = {};
  zinciri.filter((z) => z.karar === 'onay').forEach((z) => { onaylanan[z.rol] = z; });
  const currentRol = onay?.current?.rol_etiket;
  const red = zinciri.find((z) => z.karar === 'red');
  const items = chain.map((s) => {
    const done = onaylanan[s.rol_etiket];
    let status = 'wait';
    if (done) status = 'finish';
    else if (s.rol_etiket === currentRol) status = red ? 'error' : 'process';
    return {
      title: s.ad || ROL_LABEL[s.rol_etiket] || s.rol_etiket,
      description: done ? `${done.ad || ''}${done.tarih ? ` · ${done.tarih}` : ''}`
        : (s.rol_etiket === currentRol ? 'Bekliyor' : ''),
      status,
    };
  });
  const current = items.findIndex((i) => i.status === 'process');
  return (
    <>
      <Steps size="small" responsive items={items} current={current < 0 ? items.length : current} />
      {red && (
        <div style={{ marginTop: 8, color: '#cf1322' }}>
          Reddedildi{red.sebep ? `: ${red.sebep}` : ''}{red.ad ? ` (${red.ad})` : ''}
        </div>
      )}
    </>
  );
}

function RejectModal({ row, onClose, onConfirm }) {
  const [sebep, setSebep] = useState('');
  useEffect(() => { setSebep(''); }, [row]);
  return (
    <Modal title="Hakediş Reddet" open={!!row} okText="Reddet" cancelText="Vazgeç"
      okButtonProps={{ danger: true }} onOk={() => onConfirm(sebep)} onCancel={onClose} destroyOnClose>
      <Input.TextArea rows={3} placeholder="Red sebebi" value={sebep} onChange={(e) => setSebep(e.target.value)} />
    </Modal>
  );
}

function HakedisCreateModal({ lookups, tevOptions = [], onClose, onDone }) {
  const api = useFinanceApi();
  const [form] = Form.useForm();
  const [kalemler, setKalemler] = useState([]);
  const [locOptions, setLocOptions] = useState([]);
  const [kalem, setKalem] = useState({ masraf_tipi: 2 });
  // Seçili lokasyondaki PLATFORMLAR (platformlar.id). Boş bırakılırsa masraf
  // raporlamada lokasyonun tüm platformlarına dağıtılır.
  const [kabinOptions, setKabinOptions] = useState([]);
  const locTimer = useRef(null);

  const wKdv = Form.useWatch('kdv_orani', form);
  const wDahil = Form.useWatch('kdv_dahil', form);
  const wTev = Form.useWatch('tevkifat_kodu', form);

  const searchLoc = (q) => {
    if (locTimer.current) clearTimeout(locTimer.current);
    locTimer.current = setTimeout(async () => {
      if (!q || q.length < 2) { setLocOptions([]); return; }
      try {
        const rows = await api.get('/accruals/search-lokasyon', { q });
        setLocOptions(rows.map((r) => ({
          value: r.MaptriksID,
          label: `${r.lokasyon_ad || ''} (${r.MaptriksID})`,
          row: r,
        })));
      } catch { /* ignore */ }
    }, 300);
  };

  const pickLoc = (val, opt) => {
    const r = opt.row || {};
    // Lokasyon değişince kabin seçimi sıfırlanır; slot numaraları lokasyona özeldir.
    setKalem((k) => ({
      ...k, maptriksID: r.MaptriksID, lokasyon_ad: r.lokasyon_ad,
      il: r.il, ilce: r.ilce, kabin: undefined,
    }));
    setKabinOptions([]);
    if (r.MaptriksID) {
      api.get('/accruals/kabin-options', { maptriksID: r.MaptriksID })
        .then((rows) => setKabinOptions(
          (rows || []).map((x) => ({ value: x.platform_id ?? x.kabin_no, label: x.label }))
        ))
        .catch(() => setKabinOptions([]));
    }
  };

  const addKalem = () => {
    if (!kalem.tutar) { message.warning('Kalem tutarı gerekli'); return; }
    setKalemler((arr) => [...arr, { ...kalem, _k: Date.now() }]);
    setKalem({ masraf_tipi: 2 });
  };

  const removeKalem = (k) => setKalemler((arr) => arr.filter((x) => x._k !== k));

  const toplam = kalemler.reduce((s, k) => s + (Number(k.tutar) || 0), 0);
  const tevOran = tevOptions.find((t) => t.kod === wTev)?.oran || 0;
  const calc = hesaplaTutar(toplam, wKdv ?? 20, wDahil ? 1 : 0, tevOran);

  const submit = async () => {
    let h;
    try { h = await form.validateFields(); } catch { return; }
    if (!kalemler.length) { message.warning('En az bir kalem ekleyin'); return; }
    try {
      await api.post('/accruals', {
        cari_id: h.cari_id,
        firma_id: h.firma_id,
        tarih: dayjs(h.tarih).format('YYYY-MM-DD'),
        kdv_orani: h.kdv_orani,
        kdv_dahil: h.kdv_dahil ? 1 : 0,
        tevkifat_kodu: h.tevkifat_kodu || null,
        sat_no: h.sat_no,
        kalemler: kalemler.map((k) => ({
          maptriksID: k.maptriksID, lokasyon_ad: k.lokasyon_ad, il: k.il, ilce: k.ilce,
          kabin: k.kabin || 0,
          masraf_tipi: k.masraf_tipi, islem_adi: k.islem_adi,
          aciklama: k.aciklama,
          masraf_tarihi: k.masraf_tarihi ? dayjs(k.masraf_tarihi).format('YYYY-MM-DD') : null,
          tutar: k.tutar,
        })),
      });
      message.success('Hakediş kaydedildi');
      onDone();
    } catch (e) {
      message.error(e?.response?.data?.detail || 'Kaydedilemedi');
    }
  };

  return (
    <Modal title="Yeni Hakediş" open onOk={submit} onCancel={onClose} okText="Kaydet" cancelText="Vazgeç"
      width="min(900px, calc(100vw - 32px))" destroyOnClose>
      <Form form={form} layout="vertical" initialValues={{ tarih: dayjs(), kdv_orani: 20, kdv_dahil: 0 }}>
        <Space wrap>
          <Form.Item name="cari_id" label="Cari" rules={[{ required: true }]} style={{ minWidth: 220 }}>
            <Select showSearch optionFilterProp="label" options={entityOptions(lookups, 'cari')} />
          </Form.Item>
          <Form.Item name="firma_id" label="Firma" rules={[{ required: true }]} style={{ minWidth: 200 }}>
            <Select showSearch optionFilterProp="label" options={entityOptions(lookups, 'firma')} />
          </Form.Item>
          <Form.Item name="tarih" label="Tarih" rules={[{ required: true }]}>
            <DatePicker format="DD.MM.YYYY" />
          </Form.Item>
          <Form.Item name="kdv_orani" label="KDV %"><InputNumber min={0} max={100} /></Form.Item>
          <Form.Item name="kdv_dahil" label="KDV">
            <Radio.Group optionType="button" buttonStyle="solid"
              options={[{ value: 0, label: 'Hariç' }, { value: 1, label: 'Dahil' }]} />
          </Form.Item>
          <Form.Item name="tevkifat_kodu" label="Tevkifat" style={{ minWidth: 180 }}>
            <Select allowClear placeholder="Tevkifat yok"
              options={tevOptions.map((t) => ({ value: t.kod, label: t.ad }))} />
          </Form.Item>
          <Form.Item name="sat_no" label="SAT No" rules={[{ required: true, message: 'SAT kodu zorunludur' }]}>
            <Input />
          </Form.Item>
          {/* Fatura kodu burada girilmez: fatura geldikten SONRA Satın Alma
              ekranındaki "Fatura Kodu" butonuyla işlenir (13.08 revizyonu). */}
        </Space>
      </Form>

      <Divider>Kalem Ekle</Divider>
      <Space wrap align="end" style={{ marginBottom: 12 }}>
        <div>
          <div style={{ fontSize: 12 }}>Lokasyon</div>
          <AutoComplete style={{ width: 240 }} options={locOptions} onSearch={searchLoc}
            onSelect={pickLoc} placeholder="Lokasyon ara (ad / MaptriksID)"
            value={kalem.lokasyon_ad}
            onChange={(v) => setKalem((k) => ({ ...k, lokasyon_ad: v }))} />
        </div>
        <div>
          <div style={{ fontSize: 12 }}>Platform</div>
          <Select style={{ width: 150 }} allowClear placeholder="Tümü (dağıt)"
            value={kalem.kabin} options={kabinOptions}
            disabled={!kalem.maptriksID}
            onChange={(v) => setKalem((k) => ({ ...k, kabin: v }))} />
        </div>
        <div>
          <div style={{ fontSize: 12 }}>Masraf Tipi</div>
          <Select style={{ width: 150 }} value={kalem.masraf_tipi}
            onChange={(v) => setKalem((k) => ({ ...k, masraf_tipi: v }))}
            options={Object.entries(MASRAF_TIPI).map(([v, l]) => ({ value: Number(v), label: l }))} />
        </div>
        <div>
          <div style={{ fontSize: 12 }}>İşlem Adı</div>
          <Input style={{ width: 180 }} value={kalem.islem_adi}
            onChange={(e) => setKalem((k) => ({ ...k, islem_adi: e.target.value }))} />
        </div>
        <div>
          <div style={{ fontSize: 12 }}>Açıklama</div>
          <Input style={{ width: 220 }} value={kalem.aciklama} placeholder="Kalem açıklaması"
            onChange={(e) => setKalem((k) => ({ ...k, aciklama: e.target.value }))} />
        </div>
        <div>
          <div style={{ fontSize: 12 }}>Masraf Tarihi</div>
          <DatePicker format="DD.MM.YYYY" value={kalem.masraf_tarihi}
            onChange={(d) => setKalem((k) => ({ ...k, masraf_tarihi: d }))} />
        </div>
        <div>
          <div style={{ fontSize: 12 }}>Tutar</div>
          <InputNumber {...inputNumberMoneyProps} style={{ width: 120 }} step={0.01} value={kalem.tutar}
            onChange={(v) => setKalem((k) => ({ ...k, tutar: v }))} />
        </div>
        <Button icon={<PlusOutlined />} onClick={addKalem}>Ekle</Button>
      </Space>

      <Table rowKey="_k" size="small" pagination={false} dataSource={kalemler}
        columns={[
          { title: 'Lokasyon', dataIndex: 'lokasyon_ad', ellipsis: true },
          { title: 'Platform', dataIndex: 'kabin', width: 90, render: (v) => (v ? `#${v}` : 'Tümü') },
          { title: 'Masraf', dataIndex: 'masraf_tipi', render: (v) => MASRAF_TIPI[v] || v },
          { title: 'İşlem', dataIndex: 'islem_adi', ellipsis: true },
          { title: 'Açıklama', dataIndex: 'aciklama', ellipsis: true },
          { title: 'Tutar', dataIndex: 'tutar', render: (v) => fmtAmount(v, 'TRY') },
          {
            title: '', key: 'x', width: 50,
            render: (_, r) => <Button size="small" danger icon={<DeleteOutlined />} onClick={() => removeKalem(r._k)} />,
          },
        ]}
        summary={() => (
          <Table.Summary.Row>
            <Table.Summary.Cell index={0} colSpan={5}><b>Kalem Toplam</b></Table.Summary.Cell>
            <Table.Summary.Cell index={1}><b>{fmtAmount(toplam, 'TRY')}</b></Table.Summary.Cell>
            <Table.Summary.Cell index={2} />
          </Table.Summary.Row>
        )} />

      <div style={{ marginTop: 12, display: 'flex', justifyContent: 'flex-end' }}>
        <Descriptions size="small" column={1} bordered style={{ width: '100%', maxWidth: 360 }} labelStyle={{ width: 170 }}>
          <Descriptions.Item label="Matrah (KDV hariç)">{fmtAmount(calc.matrah, 'TRY')}</Descriptions.Item>
          <Descriptions.Item label={`KDV (%${wKdv ?? 20})`}>{fmtAmount(calc.kdv, 'TRY')}</Descriptions.Item>
          {tevOran > 0 && (
            <Descriptions.Item label={`Tevkifat (${wTev})`}>−{fmtAmount(calc.tevkifat, 'TRY')}</Descriptions.Item>
          )}
          <Descriptions.Item label="Ödenecek"><b>{fmtAmount(calc.odenecek, 'TRY')}</b></Descriptions.Item>
        </Descriptions>
      </div>
    </Modal>
  );
}

function HakedisOnayConfigModal({ onClose }) {
  const api = useFinanceApi();
  const [routing, setRouting] = useState([]);
  const [rolPoz, setRolPoz] = useState({ operasyon: [], satinalma: [], finans: [], yonetim: [] });
  const [rolKul, setRolKul] = useState({ operasyon: [], satinalma: [], finans: [], yonetim: [] });
  const [pozisyonlar, setPozisyonlar] = useState([]);
  const [kullanicilar, setKullanicilar] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get('/approval-config').then((r) => {
      setRouting((r.routing || []).map((x, i) => ({ ...x, _k: x.id || `r${i}` })));
      const rp = { operasyon: [], satinalma: [], finans: [], yonetim: [] };
      (r.rol_pozisyon || []).forEach((x) => {
        if (!rp[x.rol_etiket]) rp[x.rol_etiket] = [];
        rp[x.rol_etiket].push(x.pozisyon_id);
      });
      setRolPoz(rp);
      const rk = { operasyon: [], satinalma: [], finans: [], yonetim: [] };
      (r.rol_kullanici || []).forEach((x) => {
        if (!rk[x.rol_etiket]) rk[x.rol_etiket] = [];
        rk[x.rol_etiket].push(x.kullanici_no);
      });
      setRolKul(rk);
      setPozisyonlar(r.pozisyonlar || []);
      setKullanicilar(r.kullanicilar || []);
    }).catch(() => message.error('Yapılandırma alınamadı')).finally(() => setLoading(false));
  }, []);

  const setRow = (k, field, val) => setRouting((arr) => arr.map((r) => (r._k === k ? { ...r, [field]: val } : r)));
  const addRow = () => setRouting((arr) => [
    ...arr,
    { _k: `n${arr.length}_${Math.max(0, ...arr.map((r) => r.adim_sira || 0))}`, tutar_min: 0, tutar_max: null, adim_sira: 1, rol_etiket: 'operasyon', ad: '' },
  ]);
  const delRow = (k) => setRouting((arr) => arr.filter((r) => r._k !== k));

  const save = async () => {
    setSaving(true);
    try {
      const rol_pozisyon = [];
      Object.entries(rolPoz).forEach(([rol, ids]) => (ids || []).forEach((pid) => rol_pozisyon.push({ rol_etiket: rol, pozisyon_id: pid })));
      const rol_kullanici = [];
      Object.entries(rolKul).forEach(([rol, ids]) => (ids || []).forEach((uid) => rol_kullanici.push({ rol_etiket: rol, kullanici_no: uid })));
      await api.post('/approval-config', {
        modul: 'hakedis',
        routing: routing.map((r) => ({
          tutar_min: r.tutar_min || 0, tutar_max: r.tutar_max, adim_sira: r.adim_sira || 1,
          rol_etiket: r.rol_etiket, ad: r.ad,
        })),
        rol_pozisyon,
        rol_kullanici,
      });
      message.success('Onay yapılandırması kaydedildi');
      onClose();
    } catch (e) {
      message.error(e?.response?.data?.detail || 'Kaydedilemedi');
    } finally {
      setSaving(false);
    }
  };

  const ROLLER = ['operasyon', 'satinalma', 'finans', 'yonetim'];
  const rolOptions = ROLLER.map((r) => ({ value: r, label: ROL_LABEL[r] }));
  const pozOptions = pozisyonlar.map((p) => ({ value: p.id, label: p.ad }));
  const kulOptions = kullanicilar.map((u) => ({ value: u.id, label: u.ad_soyad }));

  const rCols = [
    {
      title: 'Tutar Min', dataIndex: 'tutar_min', width: 130,
      render: (v, r) => <InputNumber {...inputNumberMoneyProps} style={{ width: '100%' }} value={v} min={0} step={100000} onChange={(x) => setRow(r._k, 'tutar_min', x)} />,
    },
    {
      title: 'Tutar Max (boş=∞)', dataIndex: 'tutar_max', width: 150,
      render: (v, r) => <InputNumber {...inputNumberMoneyProps} style={{ width: '100%' }} value={v} min={0} step={100000} placeholder="∞" onChange={(x) => setRow(r._k, 'tutar_max', x)} />,
    },
    {
      title: 'Adım', dataIndex: 'adim_sira', width: 70,
      render: (v, r) => <InputNumber style={{ width: '100%' }} value={v} min={1} onChange={(x) => setRow(r._k, 'adim_sira', x)} />,
    },
    {
      title: 'Rol', dataIndex: 'rol_etiket', width: 150,
      render: (v, r) => <Select style={{ width: '100%' }} value={v} options={rolOptions} onChange={(x) => setRow(r._k, 'rol_etiket', x)} />,
    },
    {
      title: 'Ad', dataIndex: 'ad',
      render: (v, r) => <Input value={v} onChange={(e) => setRow(r._k, 'ad', e.target.value)} />,
    },
    {
      title: '', key: 'x', width: 44,
      render: (_, r) => <Button size="small" danger icon={<DeleteOutlined />} onClick={() => delRow(r._k)} />,
    },
  ];

  return (
    <Modal title="Onay Akışı Yapılandırması" open width="min(920px, calc(100vw - 32px))" onOk={save} onCancel={onClose}
      okText="Kaydet" cancelText="Kapat" confirmLoading={saving} destroyOnClose>
      <div style={{ color: '#888', marginBottom: 8, fontSize: 12 }}>
        Ödenecek tutar bandına göre onay adımları. Bant kuralı: <b>tutar_min ≤ ödenecek &lt; tutar_max</b>. Adımlar sırayla işler.
      </div>
      <Table rowKey="_k" size="small" pagination={false} loading={loading} dataSource={routing} columns={rCols} scroll={{ x: 'max-content' }} />
      <Button icon={<PlusOutlined />} size="small" style={{ marginTop: 8 }} onClick={addRow}>Adım Ekle</Button>

      <Divider>Rol → Kişi</Divider>
      <div style={{ color: '#888', marginBottom: 8, fontSize: 12 }}>Her rolü hangi <b>kişilerin</b> onaylayabileceğini seçin (süreç akışına göre). Pozisyonu olmayanlar da atanabilir.</div>
      {ROLLER.map((rol) => (
        <div key={rol} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
          <div style={{ width: 110, fontWeight: 600 }}>{ROL_LABEL[rol]}</div>
          <Select mode="multiple" style={{ flex: 1 }} allowClear showSearch optionFilterProp="label"
            placeholder="Kişi seç..." value={rolKul[rol] || []} options={kulOptions}
            onChange={(vals) => setRolKul((s) => ({ ...s, [rol]: vals }))} />
        </div>
      ))}

      <Divider>Rol → Pozisyon <span style={{ color: '#888', fontWeight: 400, fontSize: 12 }}>(o pozisyondaki herkes)</span></Divider>
      <div style={{ color: '#888', marginBottom: 8, fontSize: 12 }}>Kişi bazlıya ek olarak, bir pozisyondaki herkesi role bağlamak için. (Yönetim tüm adımları onaylayabilir.)</div>
      {ROLLER.map((rol) => (
        <div key={rol} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
          <div style={{ width: 110, fontWeight: 600 }}>{ROL_LABEL[rol]}</div>
          <Select mode="multiple" style={{ flex: 1 }} allowClear showSearch optionFilterProp="label"
            placeholder="Pozisyon seç..." value={rolPoz[rol] || []} options={pozOptions}
            onChange={(vals) => setRolPoz((s) => ({ ...s, [rol]: vals }))} />
        </div>
      ))}
    </Modal>
  );
}
